import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useCart } from '../../../Contexts/CartContext';
import { useCartWithListings } from '../../../hooks/useCartWithListings';
import { useFulfilmentMode } from '../../../hooks/useFulfilmentMode';
import { showToast } from '../../../utils/toast';
import { formatCurrency } from '../../../utils/common';
import { routes } from '../../../routes/routes';
import { CartOverlayView } from './CartOverlay.view';

/**
 * The cart drawer, the container. Cart state, localStorage, the stock guard
 * and the navigation to `/cart`.
 *
 * The public API is unchanged, because `SellerDashboard/NavigationHeader`
 * mounts this globally and that file is not in this batch:
 *
 *   <CartOverlay isOpen={showCartOverlay} onClose={...} />
 *
 * Everything visual lives in `CartOverlay.view.jsx`, which is pure.
 *
 * EVERY BEHAVIOUR IS PRESERVED:
 * - The display list is flattened from the FRESH merge-hook `cartData`, never
 *   from raw localStorage, which only carries `{ listing_id, quantity }`.
 * - Quantity changes mutate the MINIMAL cart keyed by `listing_id`, write
 *   `cart_<id>` / `cart_guest`, and then keep `orderItems_*` in step when the
 *   open handoff belongs to the same seller. Dropping to zero removes the item.
 * - The live-stock ceiling and its toast copy are unchanged.
 */
const CartOverlay = ({ isOpen, onClose }) => {
  const fulfilmentMode = useFulfilmentMode();
  const { setCartItems, setCheckoutData, cartItems } = useCart();
  const { cartData, isLoading } = useCartWithListings();
  const { user } = useAuth();
  const navigate = useNavigate();

  const getCartKey = () => (user ? `cart_${user.id || user._id}` : 'cart_guest');

  /* Flatten the fresh cart into view-ready lines. Prices are formatted HERE so
     the shared `CartLine` never formats currency and can never be handed
     anything but Rands. */
  const items = useMemo(
    () =>
      Object.entries(cartData || {}).flatMap(([sellerId, products]) =>
        products.map((product) => {
          const listing = product.card_listing;
          const card = listing?.card || {};
          const cardId = listing?.card_id ?? card.id;

          return {
            id: listing.id,
            sellerId,
            image: listing.images?.[0]?.image,
            set: card.set?.name || card.set_name,
            name: listing.card_name || card.name || 'Card',
            href: cardId
              ? `${routes.MARKETPLACE}/card/${cardId}/${listing.id}`
              : undefined,
            condition: listing.condition,
            /* Sent by `getListingsByIds` and dropped here until now. */
            finish: listing.finish,
            unitPrice: formatCurrency(listing.price),
            quantity: product.quantity,
            /* The seller's live stock, the ceiling the stepper disables at. */
            max: listing.quantity,
            lineTotal: formatCurrency(listing.price * product.quantity),
            price: listing.price,
          };
        })
      ),
    [cartData]
  );

  const handleRemoveItem = (sellerId, itemId) => {
    const updatedCart = { ...cartItems };
    if (!updatedCart[sellerId]) return;

    const remaining = updatedCart[sellerId].filter((it) => it.listing_id !== itemId);

    if (remaining.length > 0) {
      updatedCart[sellerId] = remaining;
    } else {
      delete updatedCart[sellerId];
    }

    localStorage.setItem(getCartKey(), JSON.stringify(updatedCart));
    setCartItems(updatedCart);
  };

  const handleQuantityChange = (sellerId, itemId, newQuantity) => {
    if (newQuantity <= 0) return handleRemoveItem(sellerId, itemId);

    const currentItem = items.find((item) => item.id === itemId);
    if (!currentItem) return;

    if (newQuantity > currentItem.max) {
      showToast.error(
        `Cannot add more than ${currentItem.max} items. Only ${currentItem.max} available in stock.`
      );
      return;
    }

    // Update the MINIMAL cart ({ listing_id, quantity }), keyed by listing_id.
    const updatedCart = { ...cartItems };
    updatedCart[sellerId] = (updatedCart[sellerId] || []).map((it) =>
      it.listing_id === itemId ? { ...it, quantity: newQuantity } : it
    );
    localStorage.setItem(getCartKey(), JSON.stringify(updatedCart));
    setCartItems(updatedCart);

    // Keep the open checkout handoff in step, if it is this seller's bucket.
    const orderItemsKey = user
      ? `orderItems_${user.id || user._id}`
      : 'orderItems_guest';
    const storedOrderItems = JSON.parse(localStorage.getItem(orderItemsKey)) || {};

    if (storedOrderItems.sellerId === sellerId && storedOrderItems.items) {
      storedOrderItems.items = storedOrderItems.items.map((item) =>
        item.listing_id === itemId ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem(orderItemsKey, JSON.stringify(storedOrderItems));
      setCheckoutData(storedOrderItems);
    }
  };

  const subtotal = formatCurrency(
    items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  );

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  // The RAW (minimal) cart decides whether this is loading, since the fresh
  // `items` list is empty until the listings fetch resolves.
  const isRawCartEmpty = Object.keys(cartItems || {}).length === 0;

  return (
    <CartOverlayView
      /* Only promise a courier when we know one is coming. */
      shipsNationwide={fulfilmentMode === 'shipping'}
      open={Boolean(isOpen)}
      onClose={onClose}
      items={items}
      subtotal={subtotal}
      loading={isLoading && !isRawCartEmpty}
      onQuantityChange={handleQuantityChange}
      onRemove={handleRemoveItem}
      onViewCart={handleViewCart}
    />
  );
};

export default CartOverlay;
